// Pack manager (rebuild.md §7.8, §9.11): a chapter pack is that stage's 720p excerpt(s), posters, VTT and
// comic pages, downloaded in the page into Cache Storage `pack-{stageId}`. The service worker serves cached
// video with Range support. Packs are versioned by manifest hash; stale packs are offered for refresh,
// never silently deleted. Records live in IndexedDB (`pack:{stageId}`).
import { signal } from '@preact/signals';
import { kvDel, kvGet, kvSet } from '@save/store.ts';
import { loadMediaManifest, mediaManifest } from './manifest.ts';

export interface PackRecord {
  stageId: string;
  hash: string;
  bytes: number;
  files: string[];
  at: string;
}

export type PackState =
  | { status: 'none' }
  | { status: 'downloading'; done: number; total: number }
  | { status: 'ready'; record: PackRecord; stale: boolean }
  | { status: 'error'; message: string };

export const packs = signal<Record<string, PackState>>({});

const setState = (id: string, s: PackState): void => {
  packs.value = { ...packs.value, [id]: s };
};
const cacheName = (id: string) => `pack-${id}`;
let persistAsked = false;

export async function refreshPackStates(): Promise<void> {
  const m = await loadMediaManifest();
  const next: Record<string, PackState> = {};
  for (const id of Object.keys(m.packs)) {
    const rec = await kvGet<PackRecord>(`pack:${id}`);
    next[id] = rec ? { status: 'ready', record: rec, stale: !!m.hash && rec.hash !== m.hash && !sameFiles(rec.files, m.packs[id]!.files) } : { status: 'none' };
  }
  packs.value = next;
}

const sameFiles = (a: string[], b: string[]) => a.length === b.length && a.every((f) => b.includes(f));

export function isDownloaded(stageId: string): boolean {
  return packs.value[stageId]?.status === 'ready';
}

/** Storage check before a download: returns free bytes (or null when unknown). */
export async function freeSpace(): Promise<number | null> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (!est || est.quota === undefined || est.usage === undefined) return null;
    return est.quota - est.usage;
  } catch {
    return null;
  }
}

export async function downloadPack(stageId: string): Promise<void> {
  const m = mediaManifest.value.version ? mediaManifest.value : await loadMediaManifest();
  const pack = m.packs[stageId];
  if (!pack) return setState(stageId, { status: 'error', message: 'No pack for this chapter yet.' });
  const free = await freeSpace();
  if (free !== null && free < pack.bytes * 1.1) return setState(stageId, { status: 'error', message: 'Not enough storage on this device.' });
  if (!persistAsked) {
    persistAsked = true;
    try {
      await navigator.storage?.persist?.();
    } catch {
      /* best effort */
    }
  }
  const cache = await caches.open(cacheName(stageId));
  let done = 0;
  setState(stageId, { status: 'downloading', done, total: pack.bytes });
  // The comic panel script is shared by every chapter; each pack carries a copy so the fallback works offline.
  const files = m.comic && !pack.files.includes(m.comic) ? [...pack.files, m.comic] : pack.files;
  try {
    for (const rel of files) {
      const url = `/media/${rel}`;
      let hit = await cache.match(url);
      if (!hit) {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok || res.status === 206) throw new Error(`Download failed (${res.status}).`);
        await cache.put(url, res);
        hit = await cache.match(url);
      }
      done += Number(hit?.headers.get('content-length') ?? 0);
      setState(stageId, { status: 'downloading', done, total: pack.bytes });
    }
    const record: PackRecord = { stageId, hash: m.hash ?? '', bytes: pack.bytes, files, at: new Date().toISOString() };
    await kvSet(`pack:${stageId}`, record);
    setState(stageId, { status: 'ready', record, stale: false });
  } catch (e) {
    setState(stageId, { status: 'error', message: (e as Error).message });
  }
}

export async function removePack(stageId: string): Promise<void> {
  await caches.delete(cacheName(stageId));
  await kvDel(`pack:${stageId}`);
  setState(stageId, { status: 'none' });
}

export async function downloadAll(stageIds: string[]): Promise<void> {
  for (const id of stageIds) if (!isDownloaded(id)) await downloadPack(id);
}

export const formatBytes = (b: number): string => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`);
